if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // ECSR-1008: made-to-order variants carry their ship-time phrase to checkout as the
        // "Shipping Estimate" line item property. The phrases come from the map rendered by
        // snippets/shipping-estimate-map (same rule as the cart page). The input is kept in
        // step with the chosen variant so both Add to cart and Buy it now send it.
        this.shippingEstimates = this.readShippingEstimates();
        if (this.shippingEstimates) {
          this.syncShippingEstimate();
          this.form.addEventListener('change', this.syncShippingEstimate.bind(this));
          this.addEventListener('click', this.syncShippingEstimate.bind(this), true);
        }
      }

      readShippingEstimates() {
        const mapEl = this.querySelector('script[data-shipping-estimates]');
        if (!mapEl) return null;
        try {
          return JSON.parse(mapEl.textContent) || null;
        } catch (e) {
          return null;
        }
      }

      syncShippingEstimate() {
        if (!this.shippingEstimates || !this.variantIdInput) return;

        // A backorder date already travels as "Estimated ship date"; never send both.
        const etaInput = this.form.querySelector('input[name="properties[Estimated ship date]"]');
        const hasEta = etaInput && !etaInput.disabled && etaInput.value;
        const phrase = hasEta ? '' : this.shippingEstimates[String(this.variantIdInput.value)] || '';

        let input = this.form.querySelector('input[name="properties[Shipping Estimate]"]');
        if (phrase) {
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'properties[Shipping Estimate]';
            this.form.appendChild(input);
          }
          input.value = phrase;
        } else if (input) {
          input.remove();
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        this.syncShippingEstimate();
        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                    // ECSR-1002: on the quick-add path the drawer sections are
                    // replaced here, long after the cartUpdate published in
                    // .finally() below, so the wishlist resync that event drives
                    // ran against the old markup and the new mini cart line kept
                    // an outline heart wired to add even for a saved item.
                    // Resync against the markup that was actually just rendered.
                    if (typeof window.resyncWishlistButtons === 'function') {
                      try {
                        window.resyncWishlistButtons();
                      } catch (e) {
                        console.error('Wishlist resync after quick add failed', e);
                      }
                    }
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');

            publish(PUB_SUB_EVENTS.cartUpdate).then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            });;
            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
